import React, { useState, useEffect } from 'react';
import {
  X, Check, ShieldCheck, Zap, Layers, Sparkles, Smartphone, Users,
  MessageSquare, ArrowRight, Loader, Tag, AlertCircle
} from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';

export default function CustomPlanBuilderModal({ isOpen, onClose, onOrderCreated }) {
  const companyCurrency = useCompanyCurrency();
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [catalog, setCatalog] = useState({ modules: [], resourcePricing: {} });
  const [selectedModuleIds, setSelectedModuleIds] = useState(['crm_core']);
  const [maxDevices, setMaxDevices] = useState(1);
  const [maxUsers, setMaxUsers] = useState(2);
  const [monthlyMessageLimit, setMonthlyMessageLimit] = useState(2000);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    api.get('/subscriptions/modules')
      .then((res) => {
        setCatalog(res.data);
      })
      .catch((err) => {
        alertService.error('Error', err.response?.data?.error || 'Failed to load CRM modules.');
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  // Recalculate quote whenever inputs change
  useEffect(() => {
    if (!isOpen) return;
    setCalculating(true);
    const debounceTimer = setTimeout(() => {
      api.post('/subscriptions/custom-quote', {
        moduleIds: selectedModuleIds,
        maxDevices,
        maxUsers,
        monthlyMessageLimit,
        billingCycle,
      })
        .then((res) => setQuote(res.data.quote))
        .catch(() => {})
        .finally(() => setCalculating(false));
    }, 150);

    return () => clearTimeout(debounceTimer);
  }, [isOpen, selectedModuleIds, maxDevices, maxUsers, monthlyMessageLimit, billingCycle]);

  const toggleModule = (modId, isCore) => {
    if (isCore) return; // Core CRM module cannot be deselected
    setSelectedModuleIds((prev) =>
      prev.includes(modId) ? prev.filter((id) => id !== modId) : [...prev, modId]
    );
  };

  const handleProceedToPayment = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/subscriptions/custom-order', {
        moduleIds: selectedModuleIds,
        maxDevices,
        maxUsers,
        monthlyMessageLimit,
        billingCycle,
      });

      if (onOrderCreated) {
        onOrderCreated(res.data);
      }
      onClose();
    } catch (err) {
      alertService.error('Order Error', err.response?.data?.error || 'Failed to initiate custom plan order.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in duration-150">
      <div className="bg-white rounded-xl shadow-card border border-[#D8DDE6] w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden my-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#D8DDE6] bg-[#F8FAFC]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#0176D3]/10 text-[#0176D3] flex items-center justify-center">
              <Layers size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#181818]">Custom Plan Builder</h2>
              <p className="text-xs text-[#5C5C5C]">Pick modules & resource limits tailored precisely to your business.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#5C5C5C] hover:text-[#181818] hover:bg-[#F3F4F6] rounded-lg">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20 text-[#5C5C5C] gap-2">
            <Loader size={20} className="animate-spin" /> Loading module catalog...
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Configuration Controls */}
            <div className="lg:col-span-7 space-y-6">
              {/* Billing Cycle Picker */}
              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-2 uppercase tracking-wide">
                  Billing Period
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`py-2 px-3 text-xs font-semibold rounded-lg border text-center transition-all ${
                      billingCycle === 'monthly'
                        ? 'border-[#0176D3] bg-[#0176D3]/5 text-[#0176D3]'
                        : 'border-[#D8DDE6] text-[#5C5C5C] hover:border-[#C9C9C9]'
                    }`}
                  >
                    Monthly Billing
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`py-2 px-3 text-xs font-semibold rounded-lg border text-center transition-all flex items-center justify-center gap-1.5 ${
                      billingCycle === 'yearly'
                        ? 'border-[#0176D3] bg-[#0176D3]/5 text-[#0176D3]'
                        : 'border-[#D8DDE6] text-[#5C5C5C] hover:border-[#C9C9C9]'
                    }`}
                  >
                    <span>Annual Billing</span>
                    <span className="bg-[#04844B] text-white text-[10px] px-1.5 py-0.5 rounded font-bold">20% OFF</span>
                  </button>
                </div>
              </div>

              {/* Resource Sliders */}
              <div className="bg-[#F8FAFC] border border-[#D8DDE6] rounded-lg p-4 space-y-4">
                <h3 className="text-xs font-bold text-[#181818] uppercase tracking-wide">Resource Capacity</h3>
                
                {/* WhatsApp Devices */}
                <div>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="font-medium text-[#181818] flex items-center gap-1.5">
                      <Smartphone size={13} className="text-[#0176D3]" /> WhatsApp Numbers / Devices:
                    </span>
                    <span className="font-bold text-[#181818]">{maxDevices} device{maxDevices > 1 ? 's' : ''}</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={maxDevices}
                    onChange={(e) => setMaxDevices(parseInt(e.target.value, 10))}
                    className="w-full h-1.5 bg-[#E5E7EB] rounded-lg appearance-none cursor-pointer accent-[#0176D3]"
                  />
                  <div className="flex justify-between text-[10px] text-[#706E6B] mt-1">
                    <span>1 (Included)</span>
                    <span>10 Devices</span>
                  </div>
                </div>

                {/* Team Users */}
                <div>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="font-medium text-[#181818] flex items-center gap-1.5">
                      <Users size={13} className="text-[#0176D3]" /> Team User Seats:
                    </span>
                    <span className="font-bold text-[#181818]">{maxUsers} seat{maxUsers > 1 ? 's' : ''}</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="25"
                    step="1"
                    value={maxUsers}
                    onChange={(e) => setMaxUsers(parseInt(e.target.value, 10))}
                    className="w-full h-1.5 bg-[#E5E7EB] rounded-lg appearance-none cursor-pointer accent-[#0176D3]"
                  />
                  <div className="flex justify-between text-[10px] text-[#706E6B] mt-1">
                    <span>2 (Included)</span>
                    <span>25 Seats</span>
                  </div>
                </div>

                {/* Monthly Messages */}
                <div>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="font-medium text-[#181818] flex items-center gap-1.5">
                      <MessageSquare size={13} className="text-[#0176D3]" /> Monthly WhatsApp Messages:
                    </span>
                    <span className="font-bold text-[#181818]">{monthlyMessageLimit.toLocaleString()} msgs</span>
                  </div>
                  <input
                    type="range"
                    min="2000"
                    max="50000"
                    step="2000"
                    value={monthlyMessageLimit}
                    onChange={(e) => setMonthlyMessageLimit(parseInt(e.target.value, 10))}
                    className="w-full h-1.5 bg-[#E5E7EB] rounded-lg appearance-none cursor-pointer accent-[#0176D3]"
                  />
                  <div className="flex justify-between text-[10px] text-[#706E6B] mt-1">
                    <span>2,000 (Included)</span>
                    <span>50,000 Messages</span>
                  </div>
                </div>
              </div>

              {/* Module Selection Cards */}
              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-2 uppercase tracking-wide">
                  CRM Modules & Add-Ons
                </label>
                <div className="space-y-2.5">
                  {(catalog.modules || []).map((mod) => {
                    const isSelected = selectedModuleIds.includes(mod.id);
                    return (
                      <div
                        key={mod.id}
                        onClick={() => toggleModule(mod.id, mod.isCore)}
                        className={`p-3 rounded-lg border text-left cursor-pointer transition-all flex items-start gap-3 ${
                          isSelected
                            ? 'border-[#0176D3] bg-[#0176D3]/5 shadow-sm'
                            : 'border-[#D8DDE6] bg-white hover:border-[#706E6B]'
                        } ${mod.isCore ? 'cursor-default' : ''}`}
                      >
                        <div
                          className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 shrink-0 transition-colors ${
                            isSelected
                              ? 'bg-[#0176D3] border-[#0176D3] text-white'
                              : 'border-[#D8DDE6] bg-white'
                          }`}
                        >
                          {isSelected && <Check size={12} strokeWidth={3} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-bold text-[#181818] flex items-center gap-1.5">
                              {mod.name}
                              {mod.isCore && (
                                <span className="bg-[#0176D3]/10 text-[#0176D3] text-[9px] px-1.5 py-0.2 rounded font-semibold uppercase">
                                  Base
                                </span>
                              )}
                            </span>
                            <span className="text-xs font-bold text-[#181818] shrink-0">
                              +{formatCurrency(mod.priceMonthlyInr, companyCurrency)}/mo
                            </span>
                          </div>
                          <p className="text-[11px] text-[#706E6B] mt-0.5 line-clamp-2">{mod.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right: Live Quote Summary */}
            <div className="lg:col-span-5 flex flex-col">
              <div className="sticky top-0 bg-[#F8FAFC] border border-[#D8DDE6] rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-[#D8DDE6] pb-3">
                  <h3 className="text-sm font-bold text-[#181818]">Custom Plan Summary</h3>
                  {calculating && <Loader size={14} className="animate-spin text-[#0176D3]" />}
                </div>

                {quote ? (
                  <div className="space-y-3 text-xs">
                    {/* Selected Modules Breakdown */}
                    <div>
                      <span className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider block mb-1">
                        Active Modules ({quote.breakdown.modules.length})
                      </span>
                      <div className="space-y-1">
                        {quote.breakdown.modules.map((m) => (
                          <div key={m.id} className="flex justify-between text-[#2E2E2E]">
                            <span className="truncate pr-2">{m.name}</span>
                            <span className="font-medium shrink-0">
                              {formatCurrency(m.price, companyCurrency)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Resources Breakdown */}
                    {(quote.breakdown.resources.devices.cost > 0 ||
                      quote.breakdown.resources.users.cost > 0 ||
                      quote.breakdown.resources.messages.cost > 0) && (
                      <div className="border-t border-[#D8DDE6] pt-2">
                        <span className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider block mb-1">
                          Extra Resources
                        </span>
                        <div className="space-y-1">
                          {quote.breakdown.resources.devices.cost > 0 && (
                            <div className="flex justify-between text-[#2E2E2E]">
                              <span>Extra Devices (+{quote.breakdown.resources.devices.extra})</span>
                              <span>{formatCurrency(quote.breakdown.resources.devices.cost, companyCurrency)}</span>
                            </div>
                          )}
                          {quote.breakdown.resources.users.cost > 0 && (
                            <div className="flex justify-between text-[#2E2E2E]">
                              <span>Extra Seats (+{quote.breakdown.resources.users.extra})</span>
                              <span>{formatCurrency(quote.breakdown.resources.users.cost, companyCurrency)}</span>
                            </div>
                          )}
                          {quote.breakdown.resources.messages.cost > 0 && (
                            <div className="flex justify-between text-[#2E2E2E]">
                              <span>Extra Messages (+{quote.breakdown.resources.messages.extra.toLocaleString()})</span>
                              <span>{formatCurrency(quote.breakdown.resources.messages.cost, companyCurrency)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Subtotals & Taxes */}
                    <div className="border-t border-[#D8DDE6] pt-3 space-y-1.5">
                      <div className="flex justify-between text-[#5C5C5C]">
                        <span>Monthly Equivalent</span>
                        <span>{formatCurrency(quote.monthlySubtotal, companyCurrency)}/mo</span>
                      </div>
                      {quote.billingCycle === 'yearly' && (
                        <>
                          <div className="flex justify-between text-[#5C5C5C]">
                            <span>Annual Base (12 mos)</span>
                            <span>{formatCurrency(quote.subtotalBeforeDiscount, companyCurrency)}</span>
                          </div>
                          <div className="flex justify-between text-[#04844B] font-medium">
                            <span>Annual Discount (20%)</span>
                            <span>-{formatCurrency(quote.discountAmount, companyCurrency)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-[#5C5C5C]">
                        <span>GST / Taxes (18%)</span>
                        <span>{formatCurrency(quote.taxAmount, companyCurrency)}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold text-[#181818] border-t border-[#D8DDE6] pt-2">
                        <span>Total Due</span>
                        <span className="text-[#0176D3]">
                          {formatCurrency(quote.totalAmount, companyCurrency)}
                          <span className="text-xs font-normal text-[#706E6B]">
                            /{quote.billingCycle === 'yearly' ? 'yr' : 'mo'}
                          </span>
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={handleProceedToPayment}
                      disabled={submitting}
                      className="w-full mt-4 py-2.5 px-4 bg-[#0176D3] text-white font-bold rounded-lg hover:bg-[#015FB0] disabled:opacity-50 transition-colors flex items-center justify-center gap-2 shadow-sm"
                    >
                      {submitting ? (
                        <>
                          <Loader size={16} className="animate-spin" /> Preparing Checkout...
                        </>
                      ) : (
                        <>
                          <span>Checkout & Activate</span>
                          <ArrowRight size={15} />
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="py-6 text-center text-[#706E6B] text-xs">
                    Select your desired modules to calculate price.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
